import React, { useContext } from 'react';
import { Linking, View } from 'react-native';
import { format, parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import { HorizontalRule } from '../../common/HorizontalRule';
import { StyledWebView } from '../../common/StyledWebView';
import { H2, H5, Link } from '../../text';
import { convertRecurs, convertWeekdays } from '../../../common/helpers/Event';
import Utils from '../../../common/Utils';
import LayoutContext from '../../../contexts/Layout';

const STYLED_WEB_VIEW_ID = 'eventDetails';

export const Details = ({ endDateAndTime, eventDescription, recurrences, sameDay, startDateAndTime, width, ...rest }) => {
    const { layout } = useContext(LayoutContext);
    const richTextWidth = Math.floor(width * 0.98);

    const renderRow = (name, property, label, text) => {
        if (!property && !text) {
            return null;
        }

        let content = <H5 text={text} />;

        switch (name) {
            case 'phone':
                content = <H5 onPress={() => Linking.openURL(`tel:${property}`)} text={property} wrapperStyle={{ width: undefined }} />;

                break;
            case 'link':
            case 'externalLink':
                content = <Link onPress={() => Linking.openURL(property)} text={text} />;

                break;
            case 'text':
                content = <H5 text={property} />;

                break;
            case 'email':
                content = <Link onPress={() => Linking.openURL(property)} text={layout.device.isMobile ? Utils.truncateString(text, 14) : text} />;

                break;
            case 'contact':
                content = <H5 text={text} />;

                break;
            default:
                break;
        }

        return (
            <View
                style={{
                    alignItems: 'center',
                    flex: 1,
                    flexDirection: 'row',
                    marginLeft: Math.floor(layout.styleGuide.indent * 2),
                    marginVertical: layout.styleGuide.spacer4
                }}
                dataSet={{
                    spacerBottom: `StyleGuide.Spacer4(${layout.styleGuide.spacer4})`,
                    spacerLeft: `StyleGuide.Indent*2(${Math.floor(layout.styleGuide.indent * 2)})`,
                    spacerTop: `StyleGuide.Spacer4(${layout.styleGuide.spacer4})`
                }}
            >
                <View style={{ flex: 0.3 }}>
                    <H5 text={label} />
                </View>

                <View style={{ flex: 0.7 }}>{content}</View>
            </View>
        );
    };

    const renderChildCare = () => {
        let text = 'Not Available';

        if (rest.childCareAvailable === true) {
            text = `Available, ${rest.childCareCost.formatCurrency()}`;
        }

        return text;
    };

    const renderTimeWindow = (isWholeDay, start, end) => {
        const startDate = typeof start === 'string' ? parseISO(start) : new Date(start);
        const endDate = typeof end === 'string' ? parseISO(end) : new Date(end);
        const startTime = format(startDate, 'h:mm a');
        const endTime = format(endDate, 'h:mm a');

        if (!isWholeDay) {
            return (
                <>
                    {renderRow(undefined, undefined, 'Start Time:', startTime)}
                    {renderRow(undefined, undefined, 'End Time:', endTime)}
                </>
            );
        }

        return null;
    };

    const renderRecurring = () => {
        if (rest.recurs > 0) {
            const getRecursText = () => {
                switch (rest.recurs) {
                    case 2:
                        return `Weekly on ${convertWeekdays(rest.recurWeeklyOnDays)}`;
                    case 3: {
                        if (rest.recurMonthlyOnDayOfMonth !== 0) {
                            return `Monthly on ${rest.recurMonthlyOnDayOfMonth + 1}`;
                        } else if (rest.recurMonthlyOnWeekNbr !== 0) {
                            switch (rest.recurMonthlyOnWeekNbr) {
                                case 1:
                                    return `Monthly on first ${convertWeekdays(rest.recurMonthlyOnDayOfWeek)}`;
                                case 2:
                                    return `Monthly on second ${convertWeekdays(rest.recurMonthlyOnDayOfWeek)}`;
                                case 3:
                                    return `Monthly on third ${convertWeekdays(rest.recurMonthlyOnDayOfWeek)}`;
                                case 4:
                                    return `Monthly on fourth ${convertWeekdays(rest.recurMonthlyOnDayOfWeek)}`;
                                default:
                                    break;
                            }
                        } else if (rest.recurMonthlyOnDayOfWeek !== 0) {
                            return `Monthly on ${convertWeekdays(rest.recurMonthlyOnDayOfWeek)}`;
                        }

                        break;
                    }
                    default:
                        return convertRecurs(rest.recurs);
                }

                return null;
            };

            return (
                <View style={{ flexDirection: 'row' }}>
                    <H5 text='Recurs: ' />
                    <H5 text={getRecursText()} />
                </View>
            );
        }

        return null;
    };

    return (
        <View
            style={{ marginHorizontal: layout.styleGuide.spacer2 }}
            dataSet={{ spacerLeft: `StyleGuide.Spacer2(${layout.styleGuide.spacer2})`, spacerRight: `StyleGuide.Spacer2(${layout.styleGuide.spacer2})` }}
        >
            <View style={{ width: richTextWidth }}>
                <StyledWebView html={eventDescription} nodeId={STYLED_WEB_VIEW_ID} />

                <View
                    dataSet={{ spacerBottom: `StyleGuide.Spacer3(${layout.styleGuide.spacer3})`, spacerTop: `StyleGuide.Spacer3(${layout.styleGuide.spacer3})` }}
                    style={{ marginVertical: layout.styleGuide.spacer3 }}
                >
                    <HorizontalRule color='#8897a2' marginHorizontal={0} />
                </View>
            </View>

            <View>
                {/* EVENT DETAILS */}
                <H2 style={{ flexDirection: 'row', marginVertical: 5 }} text='Event Details' />
                {renderRow(undefined, undefined, 'Date:', sameDay)}
                {renderRecurring(rest.recurs > 0)}
                {renderTimeWindow(rest.isAllDayEvent, startDateAndTime, endDateAndTime)}
                {rest.displayCost && renderRow(undefined, undefined, 'Cost:', rest.cost.formatCurrency())}
                {rest.liveViewInternalLink && renderRow('link', rest.liveViewInternalLink, 'Live View:', 'Watch Live Here')}
                {rest.liveViewExternalLink && renderRow('externalLink', rest.liveViewExternalLink, 'Live View:', 'Watch Live Here')}
                {renderRow(undefined, undefined, 'Child Care:', renderChildCare(rest.childCareAvailable, rest.cost))}
                {renderRow('phone', rest.registerPhoneNumber, 'Register:')}
                {/* VENUE DETAILS */}
                <H2 style={{ flexDirection: 'row', marginVertical: 5 }} text='Venue' />
                {renderRow(undefined, undefined, 'Venue:', rest.venueName)}
                {renderRow('text', rest.room, 'Room:')}
                {renderRow(undefined, undefined, 'Address:', rest.venueAddress)}
                {rest.mapUrl && renderRow('externalLink', rest.mapUrl, 'Map:', 'View Map & Directions')}
                {renderRow('phone', rest.venuePhoneNumber, 'Phone:')}
                {renderRow('email', rest.venueEmail, 'Email:', rest.venueEmail)}
                {!!rest.contactName && (
                    <>
                        {/* ORGANIZER */}
                        <H2 style={{ flexDirection: 'row', marginVertical: 5 }} text='Organizer' />
                        {renderRow('contact', rest.contactName, 'Contact:', rest.contactName)}
                        {renderRow('phone', rest.contactPhoneNumber, 'Phone:')}
                        {renderRow('email', rest.contactEmail, 'Email:', rest.contactEmail)}
                    </>
                )}
            </View>
        </View>
    );
};

Details.propTypes = {
    eventDescription: PropTypes.string,
    startDateAndTime: PropTypes.string,
    endDateAndTime: PropTypes.string,
    cost: PropTypes.number,
    displayCost: PropTypes.bool,
    liveViewExternalLink: PropTypes.string,
    liveViewInternalLink: PropTypes.string,
    childCareAvailable: PropTypes.bool,
    recurs: PropTypes.number,
    childCareCost: PropTypes.number,
    isAllDayEvent: PropTypes.bool,
    venueName: PropTypes.string.isRequired,
    venueAddress: PropTypes.string.isRequired,
    venuePhoneNumber: PropTypes.string,
    room: PropTypes.string,
    mapURL: PropTypes.string,
    contactEmail: PropTypes.string,
    contactName: PropTypes.string,
    contactPhoneNumber: PropTypes.string,
    maxRecurringDate: PropTypes.string,
    recurMonthlyOnDayOfMonth: PropTypes.number,
    recurMonthlyOnDayOfWeek: PropTypes.number,
    recurMonthlyOnWeekNbr: PropTypes.number,
    recurWeeklyOnDays: PropTypes.number,
    sameDay: PropTypes.node
};
